import { QueryClient, DefaultOptions } from '@tanstack/react-query';

/**
 * React Query configuration for the application
 *
 * Caching Strategy:
 * - staleTime: 5 minutes - Data is considered fresh for 5 min, no refetch needed
 * - gcTime: 10 minutes - Keep in memory for 10 min after no components use it
 * - retry: 1 - Only retry failed requests once
 * - refetchOnWindowFocus: false - Don't refetch when window regains focus
 * - refetchOnReconnect: stale only - Refetch on reconnect only if stale
 */
const queryConfig: DefaultOptions = {
  queries: {
    staleTime: 5 * 60 * 1000,        // 5 minutes - data considered fresh
    gcTime: 10 * 60 * 1000,          // 10 minutes - keep in memory
    retry: 1,                        // Only retry once on failure
    refetchOnWindowFocus: false,     // Don't refetch on window focus
    refetchOnReconnect: true,        // Refetch on reconnect if stale
  },
  mutations: {
    retry: 1,                        // Only retry mutations once
  }
};

/**
 * QueryClient instance to be used throughout the app
 * This provides:
 * - Automatic caching and deduplication
 * - Background refetching
 * - Optimistic updates support
 * - Request deduplication (multiple components using same query share one request)
 */
export const queryClient = new QueryClient({ defaultOptions: queryConfig });

/**
 * Query key factory for type-safe and consistent query keys
 * Using a factory pattern ensures:
 * - Consistent key structure across the app
 * - Easy invalidation of related queries
 * - Type safety for query keys
 */
export const queryKeys = {
  // Companies
  companies: {
    all: ['companies'] as const,
    lists: () => [...queryKeys.companies.all, 'list'] as const,
    list: (filters: Record<string, unknown>) => [...queryKeys.companies.lists(), filters] as const,
    details: () => [...queryKeys.companies.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.companies.details(), id] as const,
    stats: () => [...queryKeys.companies.all, 'stats'] as const,
    batch: (filters: Record<string, unknown>) => [...queryKeys.companies.all, 'batch', filters] as const,
    safetyStaffStatus: (id: string) => [...queryKeys.companies.all, 'safety-staff-status', id] as const,
  },

  relationshipVerifications: {
    all: ['relationship-verifications'] as const,
    myRestrictedAccess: () => [...queryKeys.relationshipVerifications.all, 'my-restricted-access'] as const,
  },

  companyRelationships: {
    all: ['company-relationships'] as const,
    myProviderPlanBlock: () => [...queryKeys.companyRelationships.all, 'my-provider-plan-block'] as const,
  },

  // Users
  users: {
    all: ['users'] as const,
    lists: () => [...queryKeys.users.all, 'list'] as const,
    // companyId is the tenant scope; pass '' or 'platform' for cross-tenant lists.
    list: (filters: Record<string, unknown>, companyId: string = '') => [...queryKeys.users.lists(), companyId, filters] as const,
    details: () => [...queryKeys.users.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.users.details(), id] as const,
    stats: () => [...queryKeys.users.all, 'stats'] as const,
    batch: (filters: Record<string, unknown>) => [...queryKeys.users.all, 'batch', filters] as const,
  },

  // Contractors
  contractors: {
    all: ['contractors'] as const,
    lists: () => [...queryKeys.contractors.all, 'list'] as const,
    list: (filters: Record<string, unknown>, companyId: string = '') => [...queryKeys.contractors.lists(), companyId, filters] as const,
    details: () => [...queryKeys.contractors.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.contractors.details(), id] as const,
    stats: () => [...queryKeys.contractors.all, 'stats'] as const,
    batch: (filters: Record<string, unknown>) => [...queryKeys.contractors.all, 'batch', filters] as const,
  },

  // Service Cards
  serviceCards: {
    all: ['serviceCards'] as const,
    lists: () => [...queryKeys.serviceCards.all, 'list'] as const,
    // Tenant-scoped — pass user/company id (or '' / 'platform' for cross-tenant views)
    list: (filters: Record<string, unknown>, companyId: string = '') => [...queryKeys.serviceCards.lists(), companyId, filters] as const,
    details: () => [...queryKeys.serviceCards.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.serviceCards.details(), id] as const,
    stats: () => [...queryKeys.serviceCards.all, 'stats'] as const,
  },

  // Initial Evaluations
  initialEvaluations: {
    all: ['initialEvaluations'] as const,
    lists: () => [...queryKeys.initialEvaluations.all, 'list'] as const,
    // Tenant-scoped — pass user/company id (or '' / 'platform' for cross-tenant views)
    list: (filters: Record<string, unknown>, companyId: string = '') => [...queryKeys.initialEvaluations.lists(), companyId, filters] as const,
    details: () => [...queryKeys.initialEvaluations.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.initialEvaluations.details(), id] as const,
    stats: () => [...queryKeys.initialEvaluations.all, 'stats'] as const,
  },

  // Critical Task Permits
  criticalTaskPermits: {
    all: ['criticalTaskPermits'] as const,
    lists: () => [...queryKeys.criticalTaskPermits.all, 'list'] as const,
    // Tenant-scoped — pass user/company id (or '' / 'platform' for cross-tenant views)
    list: (filters: Record<string, unknown>, companyId: string = '') => [...queryKeys.criticalTaskPermits.lists(), companyId, filters] as const,
    details: () => [...queryKeys.criticalTaskPermits.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.criticalTaskPermits.details(), id] as const,
    stats: () => [...queryKeys.criticalTaskPermits.all, 'stats'] as const,
    batch: (filters: Record<string, unknown>) => [...queryKeys.criticalTaskPermits.all, 'batch', filters] as const,
  },

  // Courses
  courses: {
    all: ['courses'] as const,
    lists: () => [...queryKeys.courses.all, 'list'] as const,
    // Tenant-scoped — pass user/company id (or '' / 'platform' for cross-tenant views)
    list: (filters: Record<string, unknown>, companyId: string = '') => [...queryKeys.courses.lists(), companyId, filters] as const,
    details: () => [...queryKeys.courses.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.courses.details(), id] as const,
    stats: () => [...queryKeys.courses.all, 'stats'] as const,
    batch: (filters: Record<string, unknown>) => [...queryKeys.courses.all, 'batch', filters] as const,
  },

  // Departments
  departments: {
    all: ['departments'] as const,
    lists: () => [...queryKeys.departments.all, 'list'] as const,
    list: (filters: Record<string, unknown>) => [...queryKeys.departments.lists(), filters] as const,
    byCompany: (companyId: string) => [...queryKeys.departments.all, 'company', companyId] as const,
  },

  // User Verifications
  userVerifications: {
    all: ['userVerifications'] as const,
    me: (companyId?: string) => [...queryKeys.userVerifications.all, 'me', companyId] as const,
    pending: () => [...queryKeys.userVerifications.all, 'pending'] as const,
    pendingList: (filters: Record<string, unknown>) => [...queryKeys.userVerifications.pending(), filters] as const,
    pendingUsers: () => [...queryKeys.userVerifications.all, 'pending-users'] as const,
    pendingUsersList: (filters: Record<string, unknown>) => [...queryKeys.userVerifications.pendingUsers(), filters] as const,
    user: (userId: string) => [...queryKeys.userVerifications.all, 'user', userId] as const,
    auditLog: (userId: string, filters: Record<string, unknown>) => [...queryKeys.userVerifications.all, 'auditLog', userId, filters] as const,
  },

  // Two-Factor Authentication
  twoFactor: {
    all: ['twoFactor'] as const,
    settings: (companyId: string) => [...queryKeys.twoFactor.all, 'settings', companyId] as const,
    compliance: (companyId: string) => [...queryKeys.twoFactor.all, 'compliance', companyId] as const,
  },

  // Dangerous Task Courses
  dangerousTaskCourses: {
    all: ['dangerousTaskCourses'] as const,
    byCompany: (companyId: string) => [...queryKeys.dangerousTaskCourses.all, companyId] as const,
  },

  // Incident Reports
  incidentReports: {
    all: ['incidentReports'] as const,
    lists: () => [...queryKeys.incidentReports.all, 'list'] as const,
    list: (filters: Record<string, unknown>, companyId: string = '') => [...queryKeys.incidentReports.lists(), companyId, filters] as const,
    stats: (companyId: string = '') => [...queryKeys.incidentReports.all, 'stats', companyId] as const,
  },

  // Attention Calls
  attentionCalls: {
    all: ['attentionCalls'] as const,
    lists: () => [...queryKeys.attentionCalls.all, 'list'] as const,
    list: (filters: Record<string, unknown>, companyId: string = '') => [...queryKeys.attentionCalls.lists(), companyId, filters] as const,
    stats: (companyId: string = '') => [...queryKeys.attentionCalls.all, 'stats', companyId] as const,
  },

  // Support Tickets
  supportTickets: {
    all: ['supportTickets'] as const,
    lists: () => [...queryKeys.supportTickets.all, 'list'] as const,
    list: (filters: Record<string, unknown>, companyId: string = '') => [...queryKeys.supportTickets.lists(), companyId, filters] as const,
    stats: (companyId: string = '') => [...queryKeys.supportTickets.all, 'stats', companyId] as const,
  },

  // Templates (Work Permit Templates)
  templates: {
    all: ['templates'] as const,
    lists: () => [...queryKeys.templates.all, 'list'] as const,
    // Tenant-scoped — pass user/company id (or '' / 'platform' for cross-tenant views)
    list: (filters: Record<string, unknown>, companyId: string = '') => [...queryKeys.templates.lists(), companyId, filters] as const,
    details: () => [...queryKeys.templates.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.templates.details(), id] as const,
    categories: () => [...queryKeys.templates.all, 'categories'] as const,
  },

  // Forms
  // useForms is sometimes called from screens with no active company (platform users).
  // Always pass a value — `null` becomes the literal segment 'null' in the key so the
  // query never collides with a tenant-scoped query that happens to omit the arg.
  forms: {
    all: ['forms'] as const,
    lists: () => [...queryKeys.forms.all, 'list'] as const,
    list: (filters: Record<string, unknown>, companyId: string | null = null) => [...queryKeys.forms.lists(), companyId ?? 'null', filters] as const,
    details: () => [...queryKeys.forms.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.forms.details(), id] as const,
    stats: () => [...queryKeys.forms.all, 'stats'] as const,
  },

  // Work Requests
  workRequests: {
    all: ['workRequests'] as const,
    lists: () => [...queryKeys.workRequests.all, 'list'] as const,
    list: (filters: Record<string, unknown>, companyId: string = '') => [...queryKeys.workRequests.lists(), companyId, filters] as const,
    details: () => [...queryKeys.workRequests.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.workRequests.details(), id] as const,
    stats: () => [...queryKeys.workRequests.all, 'stats'] as const,
  },

  // Guest Visits
  guestVisits: {
    all: ['guestVisits'] as const,
    lists: () => [...queryKeys.guestVisits.all, 'list'] as const,
    list: (filters: Record<string, unknown>, companyId: string = '') => [...queryKeys.guestVisits.lists(), companyId, filters] as const,
    details: () => [...queryKeys.guestVisits.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.guestVisits.details(), id] as const,
  },

  // Dashboard
  dashboard: {
    all: ['dashboard'] as const,
    superAdmin: (period: string) => ['dashboard', 'superAdmin', period] as const,
    // All dashboard keys are scoped by companyId to avoid cross-tenant cache bleed
    // when a platform user (super_admin / safety_staff) switches between client
    // companies or when impersonation/company-switch mutates the active company.
    // Callers should pass user?.company?._id (or '' / 'platform' for platform-only
    // dashboards that have no company context).
    stats: (role: string, companyId: string) => ['dashboard', 'stats', role, companyId] as const,
    activities: (companyId: string) => ['dashboard', 'activities', companyId] as const,
    alerts: (companyId: string) => ['dashboard', 'alerts', companyId] as const,
    systemHealth: (companyId: string) => ['dashboard', 'systemHealth', companyId] as const,
    revenue: (companyId: string) => ['dashboard', 'revenue', companyId] as const,
    apiUsage: (companyId: string) => ['dashboard', 'apiUsage', companyId] as const,
    serviceManagement: (companyId: string) => ['dashboard', 'serviceManagement', companyId] as const,
    riskAssessments: (companyId: string) => ['dashboard', 'riskAssessments', companyId] as const,
    incidents: (companyId: string) => ['dashboard', 'incidents', companyId] as const,
    inspections: (companyId: string) => ['dashboard', 'inspections', companyId] as const,
    contractors: (companyId: string) => ['dashboard', 'contractors', companyId] as const,
    myCourses: (companyId: string) => ['dashboard', 'myCourses', companyId] as const,
    myTasks: (companyId: string) => ['dashboard', 'myTasks', companyId] as const,
    todayStats: (companyId: string) => ['dashboard', 'todayStats', companyId] as const,
    vehicleAccess: (companyId: string) => ['dashboard', 'vehicleAccess', companyId] as const,
    team: (companyId: string) => ['dashboard', 'team', companyId] as const,
    projects: (companyId: string) => ['dashboard', 'projects', companyId] as const,
    invoices: (companyId: string) => ['dashboard', 'invoices', companyId] as const,
  },

  // Marketplace Services
  // Genuinely cross-tenant — marketplace services are a global directory.
  marketplaceServices: {
    all: ['marketplaceServices'] as const,
    lists: () => [...queryKeys.marketplaceServices.all, 'list'] as const,
    list: (filters: Record<string, unknown>) => [...queryKeys.marketplaceServices.lists(), filters] as const,
    details: () => [...queryKeys.marketplaceServices.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.marketplaceServices.details(), id] as const,
    stats: () => [...queryKeys.marketplaceServices.all, 'stats'] as const,
    categories: () => [...queryKeys.marketplaceServices.all, 'categories'] as const,
  },

  // Inspections
  inspections: {
    all: ['inspections'] as const,
    lists: () => [...queryKeys.inspections.all, 'list'] as const,
    list: (filters: Record<string, unknown>, companyId: string = '') => [...queryKeys.inspections.lists(), companyId, filters] as const,
    details: () => [...queryKeys.inspections.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.inspections.details(), id] as const,
    stats: () => [...queryKeys.inspections.all, 'stats'] as const,
  },

  // Work Orders
  workOrders: {
    all: ['workOrders'] as const,
    lists: () => [...queryKeys.workOrders.all, 'list'] as const,
    list: (filters: Record<string, unknown>, companyId: string = '') => [...queryKeys.workOrders.lists(), companyId, filters] as const,
    details: () => [...queryKeys.workOrders.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.workOrders.details(), id] as const,
    stats: () => [...queryKeys.workOrders.all, 'stats'] as const,
  },

  // Supervised Contractors
  supervisedContractors: {
    all: ['supervisedContractors'] as const,
    lists: () => [...queryKeys.supervisedContractors.all, 'list'] as const,
    list: (filters: Record<string, unknown>, companyId: string = '') => [...queryKeys.supervisedContractors.lists(), companyId, filters] as const,
  },

  // Tasks
  tasks: {
    all: ['tasks'] as const,
    lists: () => [...queryKeys.tasks.all, 'list'] as const,
    list: (filters: Record<string, unknown>, companyId: string = '') => [...queryKeys.tasks.lists(), companyId, filters] as const,
    details: () => [...queryKeys.tasks.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.tasks.details(), id] as const,
  },

  // Epics
  epics: {
    all: ['epics'] as const,
    lists: () => [...queryKeys.epics.all, 'list'] as const,
    // Tenant-scoped — pass user/company id (or '' / 'platform' for cross-tenant views)
    list: (filters: Record<string, unknown>, companyId: string = '') => [...queryKeys.epics.lists(), companyId, filters] as const,
  },

  // Teams
  teams: {
    all: ['teams'] as const,
    lists: () => [...queryKeys.teams.all, 'list'] as const,
    list: (filters: Record<string, unknown>, companyId: string = '') => [...queryKeys.teams.lists(), companyId, filters] as const,
  },

  // Platform Notifications
  // Per-user scoping is required so that impersonation / logout-login on the
  // same tab can't surface another user's unread count. Pass user._id (or '' if
  // no user is logged in — the queries will be disabled anyway).
  notifications: {
    all: ['notifications'] as const,
    unreadCount: (userId: string = '') => [...queryKeys.notifications.all, 'unreadCount', userId] as const,
    list: (filters: Record<string, unknown>, userId: string = '') => [...queryKeys.notifications.all, 'list', userId, filters] as const,
  },

  // Supervisor Transfer Requests
  supervisorTransfers: {
    all: ['supervisorTransfers'] as const,
    lists: () => [...queryKeys.supervisorTransfers.all, 'list'] as const,
    list: (filters: Record<string, unknown>) => [...queryKeys.supervisorTransfers.lists(), filters] as const,
    details: () => [...queryKeys.supervisorTransfers.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.supervisorTransfers.details(), id] as const,
  },

  // Company Theme
  companyTheme: {
    all: ['companyTheme'] as const,
    theme: (companyId: string) => [...queryKeys.companyTheme.all, 'theme', companyId] as const,
    themePublic: (companyId: string) => [...queryKeys.companyTheme.all, 'themePublic', companyId] as const,
    defaults: () => [...queryKeys.companyTheme.all, 'defaults'] as const,
    logoUrl: (companyId: string) => [...queryKeys.companyTheme.all, 'logoUrl', companyId] as const,
  },

  // Subscriptions
  subscriptions: {
    all: ['subscriptions'] as const,
    byCompany: (companyId: string) => [...queryKeys.subscriptions.all, 'byCompany', companyId] as const,
    seatUsage: (companyId: string) => [...queryKeys.subscriptions.all, 'seatUsage', companyId] as const,
  },

  // Provider self-service status (own-company Fichas/Evaluaciones/Permisos
  // gate + linked-client user-allocation budget). Resolved from the caller's
  // own active company, so keyed by that company id to invalidate correctly
  // across company-context switches.
  providerSelfServiceStatus: {
    all: ['providerSelfServiceStatus'] as const,
    byCompany: (companyId: string) => [...queryKeys.providerSelfServiceStatus.all, companyId] as const,
  },

};
